(ns ui.view.grid-opensearch.columns-modal.event
  "The events of the Edit columns modal of the grid. The state of the modal is at
   `model/columns-modal-full-path`: a map with `:tab`, `:entity-type` and `:draft`. The draft is
   edited with the functions of `ui.view.grid-opensearch.columns-model`. A save
   writes the columns and the hidden columns to the config of the grid."
  (:require
   [common.opensearch.utils :as os-utils]
   [common.schema.listing-definition :as listing-definition]
   [common.schema.system :as schema-system]
   [re-frame.core :as rf]
   [ui.state.session.cofx :as session-cofx]
   [ui.view.grid-opensearch.columns-model :as columns-model]
   [ui.view.grid-opensearch.event :as grid-event]
   [ui.view.grid-opensearch.model :as model]))

(defn editable-columns
  "Return the columns that the modal shows: the columns of the config, without the
   system fields that the grid does not show."
  [columns]
  (->> columns
       (remove schema-system/hidden-system-field?)
       vec))

(defn open-draft
  "Return the draft of the modal for a grid config. `entity-type` is the type of
   the listed entity, and `tenant-schema` is the schema of the tenant. They tell
   which columns are always visible."
  [{:keys [config entity-type tenant-schema]}]
  (let [columns (editable-columns (:columns config))]
    (columns-model/build-draft
     {:columns        columns
      :hidden-columns (:hidden-columns config)
      :locked-columns (columns-model/locked-columns
                       {:columns           columns
                        :mandatory-columns (listing-definition/grid-mandatory-field-paths entity-type)
                        :name-column?      (columns-model/name-column-fn tenant-schema)})})))

(defn column-state
  "Return the column state for AG Grid that a config has: the order of the
   columns and the hidden columns. AG Grid does not know the system fields that
   the grid does not show."
  [config]
  (let [hidden-columns (set (:hidden-columns config))]
    {:state      (mapv (fn [column]
                         {:colId (os-utils/field-path->alias column)
                          :hide  (contains? hidden-columns column)})
                       (editable-columns (:columns config)))
     :applyOrder true}))

(defn open-modal
  "Handler of `::open`. The modal has the draft of the config that the grid has
   now. A grid that has no config yet does not open the modal."
  [{:keys [db session-context]} [_ grid-id]]
  (if-let [config (get-in db (model/config-full-path grid-id))]
    (let [entity-type (get-in db (model/entity-type-full-path grid-id))]
      {:db (assoc-in db
                     (model/columns-modal-full-path grid-id)
                     {:tab         :visible
                      :entity-type entity-type
                      :draft       (open-draft {:config        config
                                                :entity-type   entity-type
                                                :tenant-schema (:tenant-schema session-context)})})})
    {}))

(rf/reg-event-fx
 ::open
 [(rf/inject-cofx ::session-cofx/session-context)]
 open-modal)

(rf/reg-event-db
 ::close
 (fn [db [_ grid-id]]
   (update-in db (model/grid-path grid-id) dissoc model/columns-modal-key)))

(rf/reg-event-db
 ::select-tab
 (fn [db [_ grid-id tab]]
   (if (get-in db (model/columns-modal-full-path grid-id))
     (assoc-in db (conj (model/columns-modal-full-path grid-id) :tab) tab)
     db)))

(defn update-draft
  "Apply `f` to the draft of the open modal of a grid with the arguments `args`.
   Nothing changes when the modal is not open."
  [{:keys [db grid-id f args]}]
  (let [draft-path (conj (model/columns-modal-full-path grid-id) :draft)]
    (if (get-in db draft-path)
      (update-in db draft-path (fn [draft]
                                 (apply f draft args)))
      db)))

(rf/reg-event-db
 ::remove-column
 (fn [db [_ grid-id column]]
   (update-draft {:db      db
                  :grid-id grid-id
                  :f       columns-model/remove-column
                  :args    [column]})))

(rf/reg-event-db
 ::add-column
 (fn [db [_ grid-id column]]
   (update-draft {:db      db
                  :grid-id grid-id
                  :f       columns-model/add-column
                  :args    [column]})))

(rf/reg-event-db
 ::move-column
 (fn [db [_ grid-id move]]
   (update-draft {:db      db
                  :grid-id grid-id
                  :f       columns-model/move-in-section
                  :args    [move]})))

(defn save-columns
  "Handler of `::save`. The config of the grid gets the columns of the draft, the
   config is saved for the user, and AG Grid gets the new order and the hidden
   columns. The grid loads its rows again only when the hidden columns change,
   because it asks for the visible columns only."
  [{:keys [db]} [_ grid-id]]
  (let [modal (get-in db (model/columns-modal-full-path grid-id))
        config (get-in db (model/config-full-path grid-id))]
    (if-not (and modal config)
      {}
      (let [next-config (columns-model/apply-draft {:config config
                                                    :draft  (:draft modal)})
            api (get-in db (model/ag-grid-api-full-path grid-id))]
        {:db (-> db
                 (assoc-in (model/config-full-path grid-id) next-config)
                 (update-in (model/grid-path grid-id) dissoc model/columns-modal-key))
         :fx (cond-> [[::grid-event/persist-config {:grid-id grid-id
                                                    :config  next-config}]]
               api
               (conj [::grid-event/apply-column-state {:api          api
                                                       :column-state (column-state next-config)}])

               (columns-model/hidden-changed? config next-config)
               (conj [:dispatch-later {:ms       10
                                       :dispatch [::grid-event/trigger-query-search grid-id]}]))}))))

(rf/reg-event-fx ::save save-columns)
